import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ChevronLeft, ChevronRight, Building2, Ruler, Calendar, Award, Filter } from 'lucide-react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
// Base URL pentru imagini
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BASE_URL}/api`;

// Categorii pentru filtrare
const CATEGORIES = [
  { id: 'all', label: 'Toate', icon: Filter },
  { id: 'terase', label: 'Terase', icon: Building2 },
  { id: 'acoperisuri', label: 'Acoperișuri', icon: Building2 },
  { id: 'fundatii', label: 'Fundații', icon: Building2 },
  { id: 'speciale', label: 'Proiecte Speciale', icon: Award },
];

// Funcție pentru a construi URL-ul imaginii
const getImageUrl = (imagePath) => {
  if (!imagePath) return 'https://via.placeholder.com/400x300?text=No+Image';
  if (imagePath.startsWith('http')) return imagePath;
  return `${BASE_URL}${imagePath}`;
};

const Portfolio = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/portfolio`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      toast.error('Eroare la încărcarea portofoliului');
    } finally {
      setLoading(false);
    }
  };

  // Filtrare proiecte
  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  const openModal = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };

  const closeModal = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedProject.images.length);
    }
  };

  const prevImage = () => {
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) => (prev - 1 + selectedProject.images.length) % selectedProject.images.length);
    }
  };

  // Obține imaginea de cover sau prima imagine
  const getCoverImage = (project) => {
    return project.cover_image || project.images?.[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SEO title="Portofoliu Lucrari Hidroizolatii" description="Portofoliu lucrari realizate - hidroizolatii terase, acoperisuri, fundatii in Iasi si Moldova. Proiecte rezidentiale si comerciale." canonical="/portofoliu" />
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portfolio-page">
      <SEO title="Portofoliu Lucrari Hidroizolatii" description="Portofoliu lucrari realizate - hidroizolatii terase, acoperisuri, fundatii in Iasi si Moldova. Proiecte rezidentiale si comerciale." canonical="/portofoliu" />
      <Navbar />
      <div className="relative pt-20 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900">
        <div className="absolute inset-0 opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 pt-12 pb-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-1 bg-cyan-500/20 text-cyan-300 rounded-full text-sm font-medium mb-4">
              PORTOFOLIU LESPEZIART
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="portfolio-title">
              Proiectele Noastre
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Explorează lucrările finalizate în Iași, Suceava, Bacău, Botoșani, Bistrița și întreaga Moldovă. 
              Expertiză și calitate în fiecare proiect de hidroizolație.
            </p>
            <p className="text-cyan-400 mt-4 font-medium">
              ✓ Toate pozele sunt reale din lucrările noastre
            </p>
          </motion.div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="sticky top-16 z-30 bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 py-4 overflow-x-auto scrollbar-hide">
            {CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  selectedCategory === category.id
                    ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-600/30'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-${category.id}`}
              >
                <category.icon className="w-4 h-4" />
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
                className={`group cursor-pointer ${project.featured ? 'md:col-span-2 lg:col-span-1' : ''}`}
                onClick={() => openModal(project)}
                data-testid={`project-${project.id}`}
              >
                <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={getImageUrl(getCoverImage(project))}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=compress&w=800';
                      }}
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                    
                    {/* Featured Badge */}
                    {project.featured && (
                      <div className="absolute top-4 left-4 px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                        <Award className="w-3 h-3" />
                        FEATURED
                      </div>
                    )}
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 right-4 px-3 py-1 bg-white/90 backdrop-blur-sm text-slate-700 text-xs font-medium rounded-full">
                      {CATEGORIES.find(c => c.id === project.category)?.label || 'Proiect'}
                    </div>

                    {/* Images count */}
                    {project.images?.length > 0 && (
                      <div className="absolute bottom-16 right-4 px-2 py-1 bg-black/60 text-white text-xs rounded">
                        {project.images.length} poze
                      </div>
                    )}
                    
                    {/* Bottom Info */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
                        {project.location && (
                          <>
                            <MapPin className="w-4 h-4" />
                            <span>{project.location}</span>
                          </>
                        )}
                        {project.area && (
                          <>
                            <span className="mx-1">•</span>
                            <Ruler className="w-4 h-4" />
                            <span>{project.area}</span>
                          </>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-white line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {project.title}
                      </h3>
                    </div>
                  </div>
                  
                  {/* Card Footer */}
                  <div className="p-4 border-t bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        {project.year && (
                          <>
                            <Calendar className="w-4 h-4" />
                            <span>{project.year}</span>
                          </>
                        )}
                      </div>
                      <span className="text-cyan-600 text-sm font-medium group-hover:underline">
                        Vezi detalii →
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-20">
            <p className="text-lg text-slate-600">Nu există proiecte în această categorie.</p>
          </div>
        )}
      </div>

      {/* Stats Section */}
      <div className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">350+</div>
              <div className="text-slate-400">Proiecte Finalizate</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">450 000+</div>
              <div className="text-slate-400">mp Hidroizolați</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">9</div>
              <div className="text-slate-400">Județe Acoperite</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">10+</div>
              <div className="text-slate-400">Ani Experiență</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ai un proiect de hidroizolație?
          </h2>
          <p className="text-cyan-100 mb-6">
            Contactează-ne pentru o consultație gratuită și o ofertă personalizată.
          </p>
          <a href="/vreau-oferta">
            <Button className="bg-white text-cyan-700 hover:bg-cyan-50 px-8 py-3 text-lg font-semibold">
              Solicită Ofertă Gratuită
            </Button>
          </a>
        </div>
      </div>

      {/* Project Detail Modal */}
      <Dialog open={!!selectedProject} onOpenChange={closeModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="project-modal">
          {selectedProject && (
            <div>
              {/* Image Gallery */}
              <div className="relative bg-slate-900">
                <img
                  src={getImageUrl(selectedProject.images?.[currentImageIndex])}
                  alt={`${selectedProject.title} - imagine ${currentImageIndex + 1}`}
                  className="w-full h-[400px] object-contain bg-black"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=compress&w=800';
                  }}
                />
                
                {/* Navigation arrows */}
                {selectedProject.images?.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); prevImage(); }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                    >
                      <ChevronLeft className="w-6 h-6 text-slate-700" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); nextImage(); }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                    >
                      <ChevronRight className="w-6 h-6 text-slate-700" />
                    </button>
                  </>
                )}
                
                {/* Image counter */}
                {selectedProject.images?.length > 0 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm">
                    {currentImageIndex + 1} / {selectedProject.images.length}
                  </div>
                )}
                
                {/* Category badge */}
                <div className="absolute top-4 left-4 px-3 py-1 bg-cyan-600 text-white text-sm font-medium rounded-full">
                  {CATEGORIES.find(c => c.id === selectedProject.category)?.label || 'Proiect'}
                </div>
              </div>

              {/* Thumbnails */}
              {selectedProject.images?.length > 1 && (
                <div className="flex gap-2 p-4 overflow-x-auto bg-slate-100">
                  {selectedProject.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        currentImageIndex === idx
                          ? 'border-cyan-500 scale-105'
                          : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`Thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Details */}
              <div className="p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {selectedProject.title}
                </h2>
                
                {/* Meta info */}
                <div className="flex flex-wrap gap-4 mb-4 text-sm text-slate-600">
                  {selectedProject.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-cyan-600" />
                      {selectedProject.location}
                    </div>
                  )}
                  {selectedProject.area && (
                    <div className="flex items-center gap-1">
                      <Ruler className="w-4 h-4 text-cyan-600" />
                      {selectedProject.area}
                    </div>
                  )}
                  {selectedProject.year && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-cyan-600" />
                      {selectedProject.year}
                    </div>
                  )}
                </div>
                
                {/* Client */}
                {selectedProject.client_name && (
                  <div className="bg-slate-50 rounded-lg p-3 mb-4">
                    <span className="text-xs text-slate-500 uppercase font-medium">Client</span>
                    <p className="font-medium text-slate-900">{selectedProject.client_name}</p>
                  </div>
                )}
                
                {/* Description */}
                {selectedProject.description && (
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {selectedProject.description}
                  </p>
                )}
                
                {/* Materials */}
                {selectedProject.materials?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-slate-900 mb-2 uppercase">Materiale Utilizate</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.materials.map((material, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-cyan-50 text-cyan-700 text-sm rounded-full"
                        >
                          {material}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <a href="/vreau-oferta" className="flex-1">
                    <Button className="w-full btn-primary">
                      Solicită Ofertă Similară
                    </Button>
                  </a>
                  <Button variant="outline" onClick={closeModal}>
                    Închide
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Portfolio;
